/*
 *   File : funds.js
 *   Author : https://evoqins.com
 *   Description : funds component
 *   Integrations : Backend API integrated
 *   Version : 1.0.4
 */
import React, { useState, useEffect } from "react";
import Icon from "../../../Components/icons";
import {
  CheckboxSelect,
  FundSelect,
  Input,
} from "../../../Components/FormElements";
import { PrimaryButton } from "../../../Components/Buttons";
import { APIService } from "../../../Service";
import { toast } from "react-toastify";

export default function Funds({
  data = [],
  onSubmit,
  errorState = {}, // { general?: string, items?: [{ fundError, fundTypeError, allocationError }] }
  setErrorState, // optional setter from parent
}) {
  const emptyGroup = {
    search: "",
    filteredFunds: [],
    selectedFund: null,
    fundType: null,
    allocation: "",
  };

  const [groups, setGroups] = useState([emptyGroup]);
  const [localError, setLocalError] = useState("");
  const [loadingIndex, setLoadingIndex] = useState(null);
  const [fundTypeOptions, setFundTypeOptions] = useState([]);
  const [loadingFundTypes, setLoadingFundTypes] = useState(false);

  // Fetch fund types on component mount
  useEffect(() => {
    fetchFundTypes();
  }, []);

  useEffect(() => {
    // populate groups from incoming prop data
    if (Array.isArray(data) && data.length) {
      const populated = data.map((f) => ({
        search: f.selectedFund?.label || "",
        filteredFunds: [],
        selectedFund: f.selectedFund || null,
        fundType: normalizeFundType(f.fundType),
        allocation: f.allocation,
      }));

      setGroups(populated);

      // ensure errorState.items length matches groups if parent provided setErrorState
      if (typeof setErrorState === "function") {
        setErrorState((prev = {}) => {
          const next = { ...prev };
          const items = Array.isArray(next.items) ? [...next.items] : [];
          while (items.length < populated.length) items.push({});
          next.items = items.slice(0, populated.length);
          return next;
        });
      }
    } else {
      setGroups([emptyGroup]);
    }
  }, [data, fundTypeOptions]);

  // read general error (prefers parent's errorState.general, fallback local)
  useEffect(() => {
    setLocalError(errorState?.general || "");
  }, [errorState?.general]);

  // Helper function to normalize fund type from API data
  const normalizeFundType = (fundType) => {
    if (!fundType) return null;

    // If already in correct format
    if (
      fundType &&
      typeof fundType === "object" &&
      fundType.value &&
      fundType.label
    ) {
      return fundType;
    }

    // If it's a string (fund type name)
    if (typeof fundType === "string") {
      const matchingOption = fundTypeOptions.find(
        (opt) => opt.label.toLowerCase() === fundType.toLowerCase(),
      );
      return matchingOption || { value: fundType, label: fundType };
    }

    // If it's a number (fund type id)
    if (typeof fundType === "number") {
      const matchingOption = fundTypeOptions.find(
        (opt) => opt.value === fundType,
      );
      return matchingOption || null;
    }

    return null;
  };

  // helper to read item-level errors
  const getItemErrors = (idx) => {
    if (!errorState || !Array.isArray(errorState.items)) return {};
    return errorState.items[idx] || {};
  };

  // clear item-level error fields + general error
  const clearItemErrors = (idx, fields = []) => {
    if (typeof setErrorState !== "function") return;
    setErrorState((prev = {}) => {
      const next = { ...prev };
      if (next.general) next.general = "";
      const items = Array.isArray(next.items) ? [...next.items] : [];
      const curr = items[idx] ? { ...items[idx] } : {};
      fields.forEach((f) => {
        if (curr[f]) curr[f] = "";
      });
      items[idx] = curr;
      next.items = items;
      return next;
    });
  };

  /* ---------- API call to fetch fund types ---------- */
  const fetchFundTypes = async () => {
    try {
      setLoadingFundTypes(true);

      const response = await APIService(
        false,
        "manage-basket/filter-asset-classes",
        {},
      );

      if (response.status_code === 200 && response.data) {
        const options = Array.isArray(response.data)
          ? response.data.map((type) => ({
              value: type.id,
              label: type.name,
            }))
          : [];

        setFundTypeOptions(options);
      }
    } catch (error) {
      console.error("Error fetching fund types:", error);
      // Fallback to default options if API fails
      setFundTypeOptions([
        { value: 1, label: "Equity" },
        { value: 3, label: "Debt" },
        { value: 4, label: "Hybrid" },
        { value: 5, label: "Other" },
      ]);
    } finally {
      setLoadingFundTypes(false);
    }
  };

  /* ---------- API call to search funds ---------- */
  const searchFunds = async (searchTerm, index) => {
    if (!searchTerm || searchTerm.trim().length < 2) return;

    const currentGroup = groups[index];
    if (!currentGroup.fundType) {
      // Show error if fund type not selected
      if (typeof setErrorState === "function") {
        setErrorState((prev = {}) => {
          const next = { ...prev };
          const items = Array.isArray(next.items) ? [...next.items] : [];
          const curr = items[index] ? { ...items[index] } : {};
          curr.fundTypeError = "Please select fund type first";
          items[index] = curr;
          next.items = items;
          return next;
        });
      }
      toast.error("Please select fund type first");
      return;
    }

    try {
      setLoadingIndex(index);

      const payload = {
        query: searchTerm.trim(),
        fund_type: currentGroup.fundType.value,
      };

      const response = await APIService(
        true,
        "manage-basket/get-fund",
        payload,
      );

      if (response.status_code === 200 && response.data?.funds) {
        const funds = Array.isArray(response.data.funds)
          ? response.data.funds.map((fund) => ({
              value: fund.fund_id,
              label: fund.fund_name,
              module: fund.fund_type || "Equity",
              image: fund.fund_image,
            }))
          : [];

        const updated = [...groups];
        updated[index].filteredFunds = funds;
        setGroups(updated);
      }
    } catch (error) {
      console.error("Error searching funds:", error);
    } finally {
      setLoadingIndex(null);
    }
  };

  /* ---------- handlers ---------- */
  const handleSearch = (i, value) => {
    const updated = [...groups];
    updated[i].search = value;
    updated[i].filteredFunds = [];
    setGroups(updated);

    // clear fund selection error while searching
    clearItemErrors(i, ["fundError"]);

    // Trigger API search
    if (value.trim().length >= 2) {
      searchFunds(value, i);
    }
  };

  const handleFundSelect = (i, fund) => {
    const updated = [...groups];
    updated[i].selectedFund = fund;
    updated[i].search = fund.label || "";
    updated[i].filteredFunds = [];
    setGroups(updated);
    clearItemErrors(i, ["fundError"]);

    // Update parent
    if (onSubmit) {
      onSubmit(updated);
    }
  };

  const handleFundType = (i, type) => {
    const updated = [...groups];
    updated[i].fundType = type;
    setGroups(updated);
    clearItemErrors(i, ["fundTypeError"]);

    // Update parent
    if (onSubmit) {
      onSubmit(updated);
    }
  };

  const handleAllocation = (i, value) => {
    const updated = [...groups];
    // store as number if not empty; else empty string
    updated[i].allocation = value === "" ? "" : Number(value);
    setGroups(updated);
    clearItemErrors(i, ["allocationError"]);

    // Calculate total allocation
    const total = updated.reduce(
      (sum, g) => sum + Number(g.allocation || 0),
      0,
    );

    // Show error if total exceeds 100
    if (total > 100) {
      if (typeof setErrorState === "function") {
        setErrorState((prev = {}) => ({
          ...(prev || {}),
          general: `Total allocation cannot exceed 100%. Current total: ${total.toFixed(2)}%`,
        }));
      } else {
        setLocalError(
          `Total allocation cannot exceed 100%. Current total: ${total.toFixed(2)}%`,
        );
      }
    } else if (total === 100) {
      // Clear error when exactly 100
      if (typeof setErrorState === "function") {
        setErrorState((prev = {}) => ({
          ...(prev || {}),
          general: "",
        }));
      } else {
        setLocalError("");
      }
    }

    // Update parent
    if (onSubmit) {
      onSubmit(updated);
    }
  };

  const addGroup = () => {
    const total = groups.reduce((sum, g) => sum + Number(g.allocation || 0), 0);
    if (total >= 100) {
      // set parent's general error if possible, otherwise local
      if (typeof setErrorState === "function") {
        setErrorState((prev = {}) => ({
          ...(prev || {}),
          general: "Total allocation reached 100%. Cannot add more funds.",
        }));
      } else {
        setLocalError("Total allocation reached 100%. Cannot add more funds.");
      }
      return;
    }
    // clear errors and add
    if (typeof setErrorState === "function") {
      setErrorState((prev = {}) => {
        const next = { ...(prev || {}) };
        const items = Array.isArray(next.items) ? [...next.items] : [];
        items.push({});
        next.items = items;
        next.general = "";
        return next;
      });
    }
    setLocalError("");
    const updated = [...groups, { ...emptyGroup }];
    setGroups(updated);

    // Update parent
    if (onSubmit) {
      onSubmit(updated);
    }
  };

  const removeGroup = (index) => {
    const updated = groups.filter((_, i) => i !== index);
    setGroups(updated);
    if (typeof setErrorState === "function") {
      setErrorState((prev = {}) => {
        const next = { ...(prev || {}) };
        const items = Array.isArray(next.items) ? [...next.items] : [];
        items.splice(index, 1);
        next.items = items;
        return next;
      });
    }

    // Recalculate total after removal
    const total = updated.reduce(
      (sum, g) => sum + Number(g.allocation || 0),
      0,
    );
    if (total <= 100) {
      if (typeof setErrorState === "function") {
        setErrorState((prev = {}) => ({
          ...(prev || {}),
          general: "",
        }));
      } else {
        setLocalError("");
      }
    }

    // Update parent
    if (onSubmit) {
      onSubmit(updated);
    }
  };

  const triggerRebalance = () => {
    const total = groups.reduce((sum, g) => sum + Number(g.allocation || 0), 0);

    // Validate that all fields are filled
    let hasErrors = false;
    const itemErrors = groups.map((g) => {
      const errors = {
        fundError: !g.selectedFund ? "Select a fund" : "",
        fundTypeError: !g.fundType ? "Select fund type" : "",
        allocationError:
          !g.allocation || Number(g.allocation) <= 0
            ? "Enter valid allocation"
            : "",
      };
      if (errors.fundError || errors.fundTypeError || errors.allocationError) {
        hasErrors = true;
      }
      return errors;
    });

    if (hasErrors) {
      if (typeof setErrorState === "function") {
        setErrorState({
          general: "Please fill all required fields",
          items: itemErrors,
        });
      } else {
        setLocalError("Please fill all required fields");
      }
      toast.error("Please fill all required fields");
      return;
    }

    if (total !== 100) {
      const message = `Total allocation must be exactly 100%. Current total: ${total.toFixed(2)}%`;
      if (typeof setErrorState === "function") {
        setErrorState((prev = {}) => ({
          ...(prev || {}),
          general: message,
        }));
      } else {
        setLocalError(message);
      }
      toast.error(message);
      return;
    }

    // All validations passed
    // clear general error
    if (typeof setErrorState === "function") {
      setErrorState((prev = {}) => ({
        ...(prev || {}),
        general: "",
        items: groups.map(() => ({
          fundError: "",
          fundTypeError: "",
          allocationError: "",
        })),
      }));
    }
    setLocalError("");

    // Show success toast
    toast.success("Test rebalance triggered");

    // Call onSubmit if provided
    if (onSubmit) {
      onSubmit(groups);
    }
  };

  return (
    <div className="d-flex flex-column pt-48 row-gap-3">
      {/* Header */}
      <div className="d-flex justify-content-between">
        <h4 className="fw-500 mb-0 fs-18 lh-20 text-eerie-black">
          Update Funds
        </h4>

        <div className="d-flex align-items-center column-gap-2">
          <Icon icon={"info"} size="14" />
          <p className="fw-500 fs-12 lh-24 text-facebook-blue mb-0">
            Total allocation should be 100%
          </p>
        </div>
      </div>

      <div className="e-glass-card d-flex flex-column rounded-16 p-3 mb-3">
        {/* z-index 2 vs actions at 1: in-flow fund menus paint above Trigger Rebalance without portal */}
        <div className="position-relative" style={{ zIndex: 2 }}>
        {groups.map((group, index) => {
          const itemErr = errorState.items?.[index] || {};
          return (
            <div
              key={index}
              className="d-flex flex-column"
              style={{
                position: "relative",
                zIndex: group.filteredFunds.length > 0 ? 6 : undefined,
              }}
            >
              <div className="row">
                {/* FUND TYPE */}
                <div className="col-3">
                  <p className="mb-0 fw-400 fs-14 pb-1">
                    Fund type <span className="text-cg-red">*</span>
                  </p>

                  <CheckboxSelect
                    options={fundTypeOptions}
                    value={group.fundType}
                    onChange={(v) => handleFundType(index, v)}
                    placeholder="Select fund type"
                    type={1}
                    error={itemErr?.fundTypeError}
                    disabled={loadingFundTypes}
                  />
                </div>
                <div className="col-6 position-relative">
                  <div className="w-100">
                    <Input
                      id={`search-${index}`}
                      type="text"
                      label="Fund name"
                      require={true}
                      placeholder="Search for Fund"
                      value={group.search}
                      handleChange={(v) => handleSearch(index, v)}
                      className="w-100 px-26 pe-3 py-10"
                      error={itemErr?.fundError}
                      leadingAccessory={
                        <span
                          className="e-input-infield-affix e-input-infield-affix--start"
                          aria-hidden
                        >
                          {loadingIndex === index ? (
                            <div
                              className="spinner-border spinner-border-sm text-oxford-blue"
                              role="status"
                            />
                          ) : (
                            <Icon icon="search" size="14" />
                          )}
                        </span>
                      }
                      afterControl={
                        group.filteredFunds.length > 0 ? (
                          <div className="position-relative w-100 lh-0">
                            <FundSelect
                              options={group.filteredFunds}
                              value={group.selectedFund}
                              onChange={(v) => handleFundSelect(index, v)}
                              placeholder="Select Fund"
                              error={itemErr?.fundError}
                            />
                          </div>
                        ) : null
                      }
                    />
                  </div>
                </div>

                {/* ALLOCATION */}
                <div className="col-3">
                  <div className="position-relative">
                    <Input
                      id={`allocation-${index}`}
                      type="number"
                      label="Fund Allocation"
                      require={true}
                      placeholder="Enter Fund Allocation"
                      value={group.allocation}
                      handleChange={(v) => handleAllocation(index, v)}
                      className="w-100 ps-3 pe-18 py-10"
                      error={itemErr?.allocationError}
                    />
                    <span
                      style={{ top: "39%", right: "3%" }}
                      className="position-absolute"
                    >
                      %{" "}
                    </span>
                  </div>
                </div>
              </div>

              {/* REMOVE BUTTON */}
              {index > 0 && (
                <div className="d-flex justify-content-end mt-2">
                  <PrimaryButton
                    icon="delete"
                    name="Remove"
                    size="18"
                    ClassName="bg-transparent text-cg-red fs-14 fw-400 px-6 py-0"
                    onPress={() => removeGroup(index)}
                  />
                </div>
              )}
            </div>
          );
        })}
        </div>

        {/* ACTIONS — lower stacking than fund rows so open menus stay clickable */}
        <div className="position-relative" style={{ zIndex: 1 }}>
        <div className="row row-gap-3">
          <div className="col-3">
            <PrimaryButton
              ClassName="bg-transparent rounded-0 lh-22 fs-14 fw-500 text-facebook-blue p-0 mt-1 e-border-bottom-dashed-blue"
              name="Add new option"
              onPress={addGroup}
            />
          </div>
          <div className="col-9">
            {/* ERROR MSG */}
            {(errorState?.general || localError) && (
              <p className="text-cg-red text-end fs-13 fw-500 mt-1 mb-0">
                {errorState?.general || localError}
              </p>
            )}
          </div>
          <div className="col-12">
            <PrimaryButton
              ClassName="bg-oxford-blue w-100 lh-24 fs-16 fw-500 text-white py-3 rounded-pill"
              name="Trigger Rebalance"
              onPress={triggerRebalance}
            />
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}
